import { useEffect, useRef } from 'react';

/**
 * Luxe atmospheric backdrop.
 * - 3 blurred radial-gradient orbs (green + gold) drift ambiently and shift on scroll (parallax).
 * - Fixed behind all content (z-index: -1).
 * - Respects prefers-reduced-motion.
 */
export default function LuxeBackdrop() {
  const orb1 = useRef(null);
  const orb2 = useRef(null);
  const orb3 = useRef(null);
  const orb4 = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    let raf = null;
    let lastY = window.scrollY;

    const tick = () => {
      const y = window.scrollY;
      // Parallax depths — different speeds for layered feel
      if (orb1.current) orb1.current.style.transform = `translate3d(0, ${y * -0.08}px, 0)`;
      if (orb2.current) orb2.current.style.transform = `translate3d(0, ${y * 0.12}px, 0)`;
      if (orb3.current) orb3.current.style.transform = `translate3d(0, ${y * -0.05}px, 0)`;
      if (orb4.current) orb4.current.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
      lastY = y;
      raf = null;
    };

    const onScroll = () => {
      if (raf == null) raf = requestAnimationFrame(tick);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    tick();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      className="luxe-backdrop"
      aria-hidden
      data-testid="luxe-backdrop"
    >
      {/* Soft mesh gradient base */}
      <div className="luxe-backdrop__base" />

      {/* Green orb top-left */}
      <div ref={orb1} className="luxe-orb luxe-orb--green-1" />
      {/* Gold orb top-right */}
      <div ref={orb2} className="luxe-orb luxe-orb--gold-1" />
      {/* Deep green orb middle-left */}
      <div ref={orb3} className="luxe-orb luxe-orb--green-2" />
      {/* Subtle gold orb bottom-right */}
      <div ref={orb4} className="luxe-orb luxe-orb--gold-2" />

      {/* Film grain — gives it that high-end editorial feel */}
      <div className="luxe-backdrop__grain" />
    </div>
  );
}
